// Small shared pieces for the courses 3D world.
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useGLTF } from '@react-three/drei';
import { getTheme, onThemeChange } from '../../utils/fonts';
import { clone as cloneSkinned } from 'three/examples/jsm/utils/SkeletonUtils.js';
import * as THREE from 'three';

/** Height of the shared platform model once fitted to 5 units wide (it measures
    0.979 x 0.074 x 0.978, so 5 * 0.074 / 0.979). Only the value used before the
    real one is measured on load — see Fitted's onFit. */
export const PLATFORM_TOP = 0.38;

export const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
export const smoothstep = (x) => { const t = clamp01(x); return t * t * (3 - 2 * t); };

/** Give interactive WebGL objects the same pointer affordance as DOM controls. */
export function usePointerCursor(active) {
  useEffect(() => {
    if (!active || typeof document === 'undefined') return undefined;
    const previous = document.body.style.cursor;
    document.body.style.cursor = 'pointer';
    return () => { document.body.style.cursor = previous; };
  }, [active]);
}

/**
 * Find a bone by name, tolerating the renaming GLTFLoader does on load: the
 * characters `[].:/` are reserved for track-binding syntax and get stripped, so
 * a rig's "mixamorig:RightHand" arrives as "mixamorigRightHand". Compare on a
 * normalised form so either spelling works.
 */
const normalise = (name) => name.replace(/[^a-z0-9]/gi, '').toLowerCase();

export function findBone(root, name) {
  const target = normalise(name);
  let found = null;
  root.traverse((o) => {
    if (!found && normalise(o.name) === target) found = o;
  });
  if (!found && process.env.NODE_ENV === 'development') {
    console.warn(`[courses] bone "${name}" not found — nothing will be attached to it.`);
  }
  return found;
}

/** Total keyframe movement per second — an idle scores near zero, a dance high. */
const motionPerSecond = (clip) => {
  let total = 0;
  for (const track of clip.tracks) {
    const values = track.values;
    const stride = values.length / track.times.length;
    for (let i = stride; i < values.length; i++) {
      total += Math.abs(values[i] - values[i - stride]);
    }
  }
  return total / Math.max(clip.duration, 0.001);
};

/**
 * Choose the idle clip. Mixamo exports arrive named "Armature|mixamo.com|Layer0",
 * "…Layer0.001", … — no clue which is which, and the order shifts every time the
 * file is re-exported. So prefer a clip that actually says "idle", and otherwise
 * take the calmest one, which is what an idle is.
 */
export function pickIdleClip(clips) {
  if (!clips || !clips.length) return null;
  const named = clips.find((c) => /idle/i.test(c.name));
  if (named) return named;

  let best = clips[0];
  let bestScore = Infinity;
  for (const clip of clips) {
    const score = motionPerSecond(clip);
    if (score < bestScore) {
      bestScore = score;
      best = clip;
    }
  }
  return best;
}

/**
 * A private copy of a GLTF scene. Every island fits/positions its own models,
 * so they can never share (and fight over) one object3D. Handles skinned
 * meshes too, which a plain `.clone()` would break.
 */
export function useClonedGLTF(path) {
  const { scene, animations } = useGLTF(path);

  const object = useMemo(() => {
    const copy = cloneSkinned(scene);
    copy.traverse((o) => {
      if (!o.isMesh) return;
      // Keep Three's normal per-mesh frustum culling. These islands are
      // separated vertically, so drawing every imported mesh even when it is
      // outside the camera was pure GPU work during course transitions.
      // Animated skinned bounds can lag behind the bones, so leave only those
      // uncullable; all static platform/prop meshes use normal culling.
      o.frustumCulled = o.isSkinnedMesh ? false : true;
      const materials = Array.isArray(o.material) ? o.material : [o.material];
      materials.forEach((m) => {
        if (!m) return;
        m.envMapIntensity = 0.85;
        // These models are exported with alphaMode BLEND even though nothing
        // about them is see-through. three.js honours that by disabling depth
        // writes, so the double-sided back faces sort over the front ones and
        // the model looks hollow. Force them back to opaque.
        if (m.transparent) {
          m.transparent = false;
          m.depthWrite = true;
          m.needsUpdate = true;
        }
      });
    });
    return copy;
  }, [scene]);

  return { object, animations };
}

/** Convenience wrapper for models with no animation. */
export function useCloned(path) {
  return useClonedGLTF(path).object;
}

/**
 * Normalises an arbitrary model: centred on XZ, resting on y = 0, and scaled so
 * its height (or largest dimension, with fit="max") equals `size`. Lets us drop
 * in models exported at any scale without hand-tuning numbers per file.
 */
export function Fitted({ object, size, fit = 'height', align = 'base', onFit, ...props }) {
  const inner = useRef();
  const onFitRef = useRef(onFit);
  onFitRef.current = onFit;

  useLayoutEffect(() => {
    const o = inner.current;
    if (!o) return;
    o.position.set(0, 0, 0);
    o.scale.setScalar(1);
    o.updateMatrixWorld(true);

    const box = new THREE.Box3().setFromObject(o);
    if (box.isEmpty()) return;
    const dim = box.getSize(new THREE.Vector3());
    const mid = box.getCenter(new THREE.Vector3());
    const base = fit === 'max' ? Math.max(dim.x, dim.y, dim.z) : dim.y;
    if (!base || !Number.isFinite(base)) return;

    const s = size / base;
    o.scale.setScalar(s);
    // "base" rests the model on y = 0 (things standing on the platform);
    // "center" puts its middle at the origin (props held by a bone).
    o.position.set(-mid.x * s, align === 'center' ? -mid.y * s : -box.min.y * s, -mid.z * s);

    // Report the real fitted height so callers can stack things on top of the
    // model instead of hard-coding an offset per asset.
    if (onFitRef.current) onFitRef.current({ height: dim.y * s, radius: (Math.max(dim.x, dim.z) * s) / 2 });
  }, [object, size, fit, align]);

  return (
    <group {...props}>
      <primitive ref={inner} object={object} />
    </group>
  );
}

/**
 * The model's own long axis, pointing from its thin end toward its bulky one.
 *
 * A bounding box can't give us this: the pickaxe is authored lying diagonally,
 * so its box is wide in both X and Z and says nothing about which way the tool
 * actually points. This runs a small PCA over the vertices instead — the
 * dominant eigenvector of their covariance *is* the long axis, whatever the
 * exporter did. Orientation then becomes "aim this at that" rather than three
 * Euler angles guessed by hand.
 */
export function longestAxis(object) {
  const points = [];
  object.updateMatrixWorld(true);
  const toLocal = new THREE.Matrix4().copy(object.matrixWorld).invert();
  const v = new THREE.Vector3();

  object.traverse((o) => {
    const pos = o.isMesh && o.geometry && o.geometry.attributes.position;
    if (!pos) return;
    const step = Math.max(1, Math.floor(pos.count / 1500)); // sampling is plenty
    for (let i = 0; i < pos.count; i += step) {
      v.fromBufferAttribute(pos, i).applyMatrix4(o.matrixWorld).applyMatrix4(toLocal);
      points.push(v.clone());
    }
  });
  if (points.length < 8) return new THREE.Vector3(0, 1, 0);

  const mean = new THREE.Vector3();
  points.forEach((p) => mean.add(p));
  mean.divideScalar(points.length);

  let xx = 0, xy = 0, xz = 0, yy = 0, yz = 0, zz = 0;
  const d = new THREE.Vector3();
  points.forEach((p) => {
    d.subVectors(p, mean);
    xx += d.x * d.x; xy += d.x * d.y; xz += d.x * d.z;
    yy += d.y * d.y; yz += d.y * d.z; zz += d.z * d.z;
  });
  const cov = new THREE.Matrix3().set(xx, xy, xz, xy, yy, yz, xz, yz, zz);

  // Power iteration converges on the dominant eigenvector.
  const axis = new THREE.Vector3(1, 0.31, 0.72).normalize();
  for (let i = 0; i < 48; i++) axis.applyMatrix3(cov).normalize();

  // PCA yields an undirected line, so decide which end is the head: the denser
  // half. A pickaxe head carries far more surface than its handle.
  let positive = 0;
  points.forEach((p) => { if (d.subVectors(p, mean).dot(axis) > 0) positive++; });
  if (positive * 2 < points.length) axis.negate();

  return axis;
}

/**
 * Fake contact shadow — a radial-gradient disc. Far cheaper than a shadow map,
 * and enough to stop the models from looking like they float.
 */
export function ShadowBlob({ radius = 2.4, opacity = 0.5, ...props }) {
  // On a light page the contact shadow is the only thing grounding the model,
  // and a shadow tuned for navy reads as barely there on white.
  const [boost, setBoost] = useState(() => (getTheme() === 'light' ? 1.45 : 1));
  useEffect(() => onThemeChange((e) => setBoost(e.detail === 'light' ? 1.45 : 1)), []);
  const uniforms = useMemo(() => ({ uOpacity: { value: opacity * boost } }), [opacity, boost]);
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} {...props}>
      <circleGeometry args={[radius, 48]} />
      <shaderMaterial
        transparent
        depthWrite={false}
        uniforms={uniforms}
        vertexShader={`
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `}
        fragmentShader={`
          uniform float uOpacity;
          varying vec2 vUv;
          void main() {
            float d = distance(vUv, vec2(0.5)) * 2.0;
            float a = pow(1.0 - clamp(d, 0.0, 1.0), 2.2);
            gl_FragColor = vec4(0.0, 0.0, 0.0, a * uOpacity);
          }
        `}
      />
    </mesh>
  );
}

/**
 * Accent halo sitting in the platform plane.
 *
 * Additive blending is what makes it glow on navy, but additive light on a
 * white page is a no-op — white plus anything is still white, so the ring
 * simply vanishes. On the light theme it draws normally instead, in the
 * accent's darker ink so it reads as a drawn line rather than a glow.
 */
export function HaloRing({ accent, accentInk, radius = 3.3, ...props }) {
  const [light, setLight] = useState(() => getTheme() === 'light');
  useEffect(() => onThemeChange((e) => setLight(e.detail === 'light')), []);

  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} {...props}>
      <ringGeometry args={[radius, radius + (light ? 0.05 : 0.06), 96]} />
      <meshBasicMaterial
        color={light ? accentInk || accent : accent}
        transparent
        opacity={light ? 0.85 : 0.55}
        blending={light ? THREE.NormalBlending : THREE.AdditiveBlending}
        depthWrite={false}
        toneMapped={false}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}
